import { useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";

export default function Volunteer() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    skills: "",
    availability: "weekends",
  });
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.city) {
      toast.error("Name, email, and city are required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/volunteers", form);
      setDone(true);
      toast.success("Welcome to the team!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="volunteer-success" className="min-h-[70vh] bg-[#0A0A0C] flex items-center">
        <div className="max-w-3xl mx-auto px-6 text-center py-20">
          <Check className="mx-auto text-[#E85D04]" size={64} />
          <h1 className="mt-8 font-serif-display text-5xl lg:text-6xl">Welcome aboard.</h1>
          <p className="mt-6 text-white/70 text-lg">A field coordinator will reach out within a week.</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="volunteer-page" className="bg-[#0A0A0C] text-white">
      <section
        className="relative min-h-[55vh] flex items-end"
        style={{
          backgroundImage: "url(https://images.pexels.com/photos/6646867/pexels-photo-6646867.jpeg?auto=compress&cs=tinysrgb&w=1800)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-[#0A0A0C]/60 to-[#0A0A0C]/40" />
        <div className="relative max-w-7xl mx-auto w-full px-6 lg:px-10 pb-16">
          <div className="text-xs tracking-[0.3em] uppercase text-[#E85D04] mb-6">Volunteer</div>
          <h1 className="font-serif-display text-5xl sm:text-7xl lg:text-8xl leading-[0.92] tracking-tighter">
            Boots on the ground. <br/><em className="not-italic text-[#E85D04]">Hands in the work.</em>
          </h1>
        </div>
      </section>

      <section className="py-20 max-w-5xl mx-auto px-6 lg:px-10">
        <form onSubmit={submit} className="border border-white/10 bg-[#121215] p-8 lg:p-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input label="Full Name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="vol-name" />
            <Input label="Email *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="vol-email" />
            <Input label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="vol-phone" />
            <Input label="City / Country *" value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="vol-city" />
            <label className="md:col-span-2 block">
              <span className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-2">Availability</span>
              <select
                value={form.availability}
                onChange={(e) => setForm({ ...form, availability: e.target.value })}
                data-testid="vol-availability"
                className="w-full bg-[#0A0A0C] border border-white/10 px-4 py-3 outline-none focus:border-[#E85D04]"
              >
                <option value="weekends">Weekends</option>
                <option value="weekdays">Weekdays</option>
                <option value="flexible">Flexible / Remote</option>
                <option value="full-time">Full-time / Field deployment</option>
              </select>
            </label>
            <label className="md:col-span-2 block">
              <span className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-2">Skills you bring</span>
              <textarea
                value={form.skills}
                onChange={(e) => setForm({ ...form, skills: e.target.value })}
                rows={4}
                data-testid="vol-skills"
                placeholder="Veterinary, transport, fostering, fundraising, social media, languages…"
                className="w-full bg-[#0A0A0C] border border-white/10 px-4 py-3 outline-none focus:border-[#E85D04] placeholder:text-white/30 resize-none"
              />
            </label>
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="vol-submit"
            className="mt-10 inline-flex items-center gap-3 bg-[#E85D04] hover:bg-[#DC2F02] text-white px-8 py-4 text-xs tracking-[0.22em] uppercase disabled:opacity-60"
          >
            {submitting ? "Submitting…" : "Join The Team"} <ArrowRight size={14} />
          </button>
        </form>
      </section>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-2">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-[#0A0A0C] border border-white/10 px-4 py-3 outline-none focus:border-[#E85D04]"
      />
    </label>
  );
}
